import {noop} from '@benchling/js-utils';
import React, {useEffect, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, AnalysisViewGraphQL, isAnalysisInputStepType} from 'analysis-tool/AnalysisTypes';
import {isAnalysisOnlyChartStep} from 'analysis-tool/charting/AnalysisChartingUtils';
import {getVisualizationConfigGQLForTableViewStep} from 'analysis-tool/data/getVisualizationConfigAndName';
import {getVisualizationConfigGQLFromChartStepConfig} from 'analysis-tool/data/StepConfigUtils';
import {getTitleForView, getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {getPersistedChartViewConfig} from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import {AnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {assertAndContinue} from 'Asserts';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import VisualizationConfigEditor from 'insights/analytics-visualizations/configs/VisualizationConfigEditor';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import * as PipelineDAO from 'pipelines/PipelineDAO';
import {assertPipelineStepIsStepType, PipelineStep} from 'pipelines/PipelineTypes';
import {
  getExpectedStepDataKind,
  getPipelineStepRawDatasOfKind,
  getSingleInputRawData,
} from 'pipelines/StepDataUtils';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItem, AnalysisRightSidebarItemType} from '../RightSidebarTypes';
import {saveChartOrTable} from '../utils/saveChartOrTable';
import EditViewChart from './edit-view/EditViewChart';
import EditViewTransforms from './edit-view/EditViewTransforms';
import RightSidebarSkeleton, {
  RightSidebarWidth,
  RightSidebarConfirmationButton,
} from './RightSidebarSkeleton';
import useMaybeDraftStep, {draftStepGlobalState} from './useMaybeDraftStep';

interface EditViewRightSidebarProps {
  analysis: AnalysisGraphQL;
  selectedRightSidebarItem: AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW>;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem | null) => void;
}

/** @package */
export const visualizationConfigState = makeUseGlobalClientState<VisualizationConfig | null>(null);

export default function EditViewRightSidebar({
  analysis,
  selectedRightSidebarItem,
  onChangeSelectedRightSidebarItem,
  onChangeSelectedItem,
}: EditViewRightSidebarProps): JSX.Element {
  const isDisabled = isAnalysisReadOnly(analysis) || isArchived(analysis);
  const view = getViewForViewReference(analysis, selectedRightSidebarItem.viewReference);
  const selectedViewNode = view?.lineage.find(
    ({step}) => step.id === selectedRightSidebarItem.viewReference.stepId
  );
  const originalStep = selectedViewNode?.step;
  assert(
    originalStep != null,
    'Expected selectedRightSidebarItem.viewReference.stepId to correspond to a step in the analysis'
  );
  const {draftStep, setDraftStep} = useMaybeDraftStep(originalStep.id, null);

  // getDefaultClientIdForView(view) changes when transforms are added, so we need to use the original step id
  const clientStateId = originalStep.id;
  const [nullableVisualizationConfig, setVisualizationConfig] =
    visualizationConfigState.useGlobalClientState(clientStateId);
  const onChangeVisualizationConfig = (newVisualizationConfig: VisualizationConfig): void => {
    setVisualizationConfig(newVisualizationConfig);
    const visualizationType = getVisualizationType(newVisualizationConfig);
    const stepType =
      visualizationType === VisualizationType.TABLE
        ? PipelineStepType.TABLE_VIEW
        : PipelineStepType.CHART_VIEW;
    const name =
      originalStep.stepType === PipelineStepType.CHART_VIEW
        ? originalStep.config?.chartName
        : originalStep.config?.viewName;

    setDraftStep({
      ...originalStep,
      stepType,
      config:
        stepType === PipelineStepType.CHART_VIEW
          ? {
              chartName: name,
              chartConfig: getPersistedChartViewConfig(newVisualizationConfig),
            }
          : {
              viewName: name,
            },
    });
  };

  const dataFrames = isAnalysisInputStepType(originalStep.stepType)
    ? getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: originalStep.outputStepDatas.flatMap(({rawDatas}) => rawDatas),
      })
    : getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: originalStep.inputStepDatas.flatMap(({rawDatas}) => rawDatas),
      });

  assertAndContinue(
    dataFrames.length >= 1,
    'Expected last node of view being edited to have at least one input',
    {
      numDataFrames: dataFrames.length,
      stepId: originalStep.id,
      originalStepType: originalStep.stepType,
      originalStepStatusCode: originalStep.statusCode,
    }
  );
  const dataFrameSpec = dataFrames[0];
  // We need to update the draft step when the input step datas change, so we can use the new data in the preview.
  // We can't just use this in onChangeVisualizationConfig, because the filter/transform section is outside of
  // the visualization config section.
  useEffect(() => {
    if (draftStep != null && !isEqual(draftStep.inputStepDatas, originalStep.inputStepDatas)) {
      setDraftStep({
        ...draftStep,
        inputStepDatas: originalStep.inputStepDatas,
      });
    }
  }, [draftStep, originalStep, setDraftStep]);

  // We need to update the draft step when the output step datas change for TABLE_VIEW draft steps, because TABLE_VIEW
  // draft steps use the output step datas for their preview.
  // This logic needs to run when there is a draft step and when an original step has finished updating after
  // adding a filter/transform step.
  // This will not wipe out previews for chart views, because chart views do not write output to the draft step.
  useEffect(() => {
    if (
      draftStep != null &&
      !isEqual(draftStep.outputStepDatas, originalStep.outputStepDatas) &&
      originalStep.statusCode === PipelineStepStatusCode.SUCCEEDED
    ) {
      setDraftStep({
        ...draftStep,
        outputStepDatas: originalStep.outputStepDatas,
      });
    }
  }, [draftStep, originalStep, setDraftStep]);

  const contextValue = useMemo(
    () => ({
      dataFramesInfos: {[SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [{dataFrameSpec, id: dataFrameSpec.id}]},
      clientStateId,
    }),
    [dataFrameSpec, clientStateId]
  );

  const confirmationButtons: Array<RightSidebarConfirmationButton> = [
    {
      text: 'Cancel',
      onClick: (): void => {
        onChangeSelectedRightSidebarItem(null);
        draftStepGlobalState.deleteState(originalStep.id);
        visualizationConfigState.deleteState(clientStateId);
      },
      isPrimary: originalStep.stepType !== PipelineStepType.CHART_VIEW,
      dataTestAttrsElement: 'cancelButton',
    },
  ];
  if (
    originalStep.stepType === PipelineStepType.CHART_VIEW ||
    originalStep.stepType === PipelineStepType.TABLE_VIEW
  ) {
    confirmationButtons.push({
      text: 'Save',
      isPrimary: true,
      onClick: async (): Promise<void> => {
        if (isAnalysisOnlyChartStep(originalStep)) {
          // TODO(BNCH-119690): Move into saveChartOrTable when we allow switching between analysis only chart
          // types and other chart types
          await withLoadingScreen(
            PipelineDAO.updateAndRunPipelineStep(analysis.id, {
              stepId: originalStep.id,
              stepConfig: draftStep?.config ?? {},
            })
          );
        } else if (nullableVisualizationConfig != null) {
          await withLoadingScreen(
            saveChartOrTable({
              analysis,
              originalChartOrTableStep: originalStep as PipelineStep<
                PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW
              >,
              newChartConfig: {
                chartConfig: getPersistedChartViewConfig(nullableVisualizationConfig),
                chartName: originalStep.config.chartName,
              },
              newVisualizationConfig: nullableVisualizationConfig,
              inputDataFrameId: dataFrameSpec.id,
              onChangeSelectedRightSidebarItem,
              onChangeSelectedItem,
              selectedRightSidebarItem,
            }),
            'Saving changes...'
          );
        }
        onChangeSelectedRightSidebarItem(null);
        visualizationConfigState.deleteState(clientStateId);
        draftStepGlobalState.deleteState(originalStep.id);
      },
      disabled: isDisabled,
      dataTestAttrsElement: 'saveButton',
    });
  }

  assert(view != null, 'Expected view to exist');
  const step = draftStep != null && draftStep.config != null ? draftStep : originalStep;
  return (
    <RightSidebarSkeleton
      title={getTitleForView(view)}
      width={RightSidebarWidth.STANDARD}
      confirmationButtons={confirmationButtons}
      dataTestAttrs={{
        component: 'EditViewRightSidebar',
        element: 'root',
      }}
    >
      <StepConfigEditorContext.Provider value={contextValue}>
        <EditViewRawData
          analysis={analysis}
          clientStateId={clientStateId}
          step={step}
          view={view}
          onChangeVisualizationConfig={onChangeVisualizationConfig}
          nullableVisualizationConfig={nullableVisualizationConfig}
          isDisabled={isDisabled}
        />
        <EditViewTransforms
          analysis={analysis}
          view={view}
          dataFrameSpec={dataFrameSpec}
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
          selectedRightSidebarItem={selectedRightSidebarItem}
          onChangeSelectedItem={onChangeSelectedItem}
        />
      </StepConfigEditorContext.Provider>
    </RightSidebarSkeleton>
  );
}

interface EditViewRawDataProps {
  analysis: AnalysisGraphQL;
  clientStateId: string;
  step: PipelineStep;
  view: AnalysisViewGraphQL;
  onChangeVisualizationConfig: (newVisualizationConfig: VisualizationConfig) => void;
  nullableVisualizationConfig: VisualizationConfig | null;
  isDisabled: boolean;
}

function getVisualizationConfig(step: PipelineStep): VisualizationConfig {
  if (step.stepType === PipelineStepType.CHART_VIEW) {
    // This assert is needed for the step.config type
    assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step);
    const {visualizationConfig} = getVisualizationConfigGQLFromChartStepConfig({
      config: step.config,
      id: step.id,
    });
    return visualizationConfig;
  }
  assert(step.stepType === PipelineStepType.TABLE_VIEW, 'Expected step type to be TABLE_VIEW');
  assertPipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, step);
  const {visualizationConfig} = getVisualizationConfigGQLForTableViewStep(step.id, step.config!);
  return visualizationConfig;
}

/** @package */
export function EditViewRawData({
  analysis,
  clientStateId,
  step,
  view,
  onChangeVisualizationConfig,
  nullableVisualizationConfig,
  isDisabled,
}: EditViewRawDataProps): JSX.Element | null {
  if (
    [PipelineStepType.CHART_VIEW, PipelineStepType.TABLE_VIEW].includes(step.stepType) &&
    !isAnalysisOnlyChartStep(step)
  ) {
    const dataFrame = getSingleInputRawData<
      PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW,
      0,
      PipelineStepDataKind.DATASET
    >(step as PipelineStep<PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW, 0>);

    const visualizationConfig = getVisualizationConfig(step);
    return (
      <VisualizationConfigEditor
        columns={dataFrame.columns}
        typeSelectorIsDisabled={isDisabled}
        disabledVisualizationTypes={[]}
        isAnalysisTool={true}
        onChangeVisualizationConfig={isDisabled ? noop : onChangeVisualizationConfig}
        visualizationConfig={nullableVisualizationConfig ?? visualizationConfig}
        useSystemNames={true}
        enableAnalysisTab={false}
        enableAggregation={false}
        enableFormatTab={undefined}
        enableFormatTabForLineChart={true}
        enableErrorBarColumn={true}
        isDisabled={isDisabled}
      />
    );
  }
  const lastNode = view.lineage[view.lineage.length - 1];
  const viewStepDataKind = getExpectedStepDataKind(
    // Sometimes step is out of sync with lastNode, so use lastNode.step
    lastNode.step,
    lastNode.stepOutputReference!.outputStepData
  );
  if (step.stepType === PipelineStepType.CHART_VIEW) {
    assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step);
    return (
      <EditViewChart
        analysis={analysis}
        clientStateId={clientStateId}
        step={step}
        view={view}
        isDisabled={isDisabled}
      />
    );
  } else if (viewStepDataKind === PipelineStepDataKind.DATASET) {
    return null;
  } else if (viewStepDataKind === PipelineStepDataKind.FILE) {
    assertAndContinue(false, 'File views are not implemented yet');
    return null;
  } else {
    throw new Error(`Unexpected step data kind: ${viewStepDataKind}`);
  }
}
